// The camera: which years are on screen (time runs left to right, every year the same width) and how tall each
// land's lane is. Every quantity is a spring, so any change (a dive, a drag, a pinch) can start, stop or turn
// around at any moment without a jump. The GPU reads the same numbers as uniforms.
//
// The scroll's two ends roll away: the time window fills the middle of the screen, and the years beyond it
// are squeezed into a narrow band at each edge, as on a scroll rolled up at both ends.

import type { Chart, TimeScale } from "@hoee/chart";
import { Spring } from "./spring.ts";

export interface Plot {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** How quickly things move, as half-lives in seconds. */
const PACE = { dive: 0.12, wheel: 0.06, lanes: 0.13, rebound: 0.09 } as const;
/** The narrowest time window, as a fraction of the whole axis: about a year. */
const SPAN_MIN = 1 / 5000;
/** The window never leaves the chart: a little room before 3000 BC, and none after AD 2000, where it ends. */
export const U_START = -0.02;
export const U_END = 1;
const SPAN_MAX = U_END - U_START;
/** Each rolled end takes this share of the width. */
export const ROLL = 0.055;
/** How far a drag may pull past either end of history before it resists completely, as a share of the window. */
const STRETCH = 0.12;

export class Camera {
  readonly chart: Chart;
  plot: Plot;
  /** Lane edges in px from the plot's top, recomputed each step. */
  readonly edges: Float64Array;
  private readonly weights: Spring[];
  /** Where each lane's two halves meet, as a fraction of its height (0.5 unless a realm's half is opened). */
  private readonly splits: Spring[];
  /** The splits as plain numbers, for the GPU. */
  readonly splitValues: Float64Array;
  /** The year at the centre of the window, and log of the window's span in u. */
  private readonly centre: Spring;
  private readonly logSpan: Spring;
  /** True time: u runs from 0 at the axis start to 1 at its end, every year the same length. */
  private readonly time: TimeScale;
  /** While zooming around a point, that year stays under that point. */
  private anchor: { year: number; at: number } | null = null;
  /** True while a hand holds the scroll: it may be pulled past its ends, and springs back on release. */
  held = false;

  constructor(chart: Chart, plot: Plot) {
    this.chart = chart;
    this.plot = plot;
    const n = chart.columns.length;
    this.edges = new Float64Array(n + 1);
    this.weights = Array.from({ length: n }, () => new Spring(0, PACE.lanes));
    this.splits = Array.from({ length: n }, () => new Spring(0.5, PACE.lanes));
    this.splitValues = new Float64Array(n).fill(0.5);
    this.time = chart.scales.true;
    this.logSpan = new Spring(Math.log(SPAN_MAX), PACE.dive);
    this.centre = new Spring(this.yearOfU((U_START + U_END) / 2), PACE.dive);
    this.layoutEdges();
  }

  // ---------- Mapping: the same arithmetic the shaders do ----------

  get span(): number {
    return Math.exp(this.logSpan.value);
  }
  /** The time window, in u. */
  get window(): { u0: number; u1: number } {
    const c = this.uOf(this.centre.value),
      s = this.span;
    return { u0: c - s / 2, u1: c + s / 2 };
  }
  /** Where the flat middle of the scroll begins and ends, in px. */
  get inner(): { x0: number; x1: number } {
    const { left, width } = this.plot;
    return { x0: left + ROLL * width, x1: left + (1 - ROLL) * width };
  }

  uOf(year: number): number {
    return this.time.toU(year);
  }

  yearOfU(u: number): number {
    return this.time.fromU(u);
  }

  /** u to px, rolling away past the window's edges. */
  xOfU(u: number): number {
    const { u0, u1 } = this.window;
    return this.plot.left + this.plot.width * rollOut((u - u0) / (u1 - u0));
  }

  xOf(year: number): number {
    return this.xOfU(this.uOf(year));
  }

  yearAt(px: number): number {
    const { u0, u1 } = this.window;
    return this.yearOfU(u0 + rollIn((px - this.plot.left) / this.plot.width) * (u1 - u0));
  }

  /** 0 on the flat of the scroll, rising to 1 deep in a rolled end. */
  rollAt(px: number): number {
    const s = (px - this.plot.left) / this.plot.width;
    return Math.min(1, Math.max(0, (ROLL - s) / ROLL, (s - (1 - ROLL)) / ROLL));
  }

  /** Lane units (lane i spans [i, i + 1]; a split lane's halves are [i, i + 0.5] and [i + 0.5, i + 1]) to px. */
  yOf(c: number): number {
    const n = this.edges.length - 1;
    const i = Math.max(0, Math.min(n - 1, Math.floor(c)));
    const a = this.edges[i] as number,
      b = this.edges[i + 1] as number;
    const f = c - i,
      s = this.splitValues[i] as number;
    const g = f <= 0.5 ? (f / 0.5) * s : s + ((f - 0.5) / 0.5) * (1 - s);
    return this.plot.top + a + (b - a) * g;
  }

  laneAt(py: number): number {
    const y = py - this.plot.top;
    const n = this.edges.length - 1;
    for (let i = 0; i < n; i++) {
      const a = this.edges[i] as number,
        b = this.edges[i + 1] as number;
      if (y < b || i === n - 1) {
        const g = (y - a) / (b - a),
          s = this.splitValues[i] as number;
        return i + (g <= s ? (0.5 * g) / s : 0.5 + (0.5 * (g - s)) / (1 - s));
      }
    }
    return n;
  }

  /** Pixels per year on the flat of the scroll, at a given year. */
  pxPerYear(year: number): number {
    return (this.time.slope(year) / this.span) * this.plot.width * (1 - 2 * ROLL);
  }

  // ---------- Moving ----------

  /** Advances every spring by dt seconds. Returns whether anything is still moving. */
  step(dt: number): boolean {
    let moving = false;
    for (const w of this.weights) moving = w.step(dt, 1e-4) || moving;
    this.splits.forEach((sp, i) => {
      moving = sp.step(dt, 1e-4) || moving;
      this.splitValues[i] = sp.value;
    });
    moving = this.logSpan.step(dt, 1e-5) || moving;
    if (this.anchor) {
      // Keep the anchored year under the pointer while the span settles.
      const c = this.uOf(this.anchor.year) - (this.anchor.at - 0.5) * this.span;
      this.centre.set(this.yearOfU(c));
      if (this.logSpan.settled(1e-5)) this.anchor = null;
    } else moving = this.centre.step(dt, 1e-3) || moving;
    // Let go of, the scroll springs back inside its ends; zooming at an end pivots on it instead.
    if (!this.held) {
      const t = this.uOf(this.centre.target),
        boundedT = this.clampCentre(t, Math.exp(this.logSpan.target));
      if (Math.abs(boundedT - t) > 1e-9) {
        this.centre.halfLife = PACE.rebound;
        this.centre.target = this.yearOfU(boundedT);
        moving = true;
      }
      if (this.anchor) {
        const c = this.uOf(this.centre.value),
          bounded = this.clampCentre(c, this.span);
        if (Math.abs(bounded - c) > 1e-9) this.centre.set(this.yearOfU(bounded));
      }
    }
    this.layoutEdges();
    return moving;
  }

  private layoutEdges(): void {
    let total = 0;
    for (const w of this.weights) total += Math.exp(w.value);
    let y = 0;
    this.edges[0] = 0;
    this.weights.forEach((w, i) => {
      y += (Math.exp(w.value) / total) * this.plot.height;
      this.edges[i + 1] = y;
    });
  }

  /** The nearest centre (in u) that keeps a window of span s on the chart. */
  private clampCentre(c: number, s: number): number {
    const lo = U_START + s / 2,
      hi = U_END - s / 2;
    return lo >= hi ? (U_START + U_END) / 2 : Math.min(hi, Math.max(lo, c));
  }

  private clampSpan(s: number): number {
    return Math.min(SPAN_MAX, Math.max(SPAN_MIN, s));
  }

  /**
   * Moves through time by a distance in px. A hand moves it at once and may pull it a little past either end,
   * against growing resistance; a wheel's notches glide and stop at the ends.
   */
  panBy(dx: number, smooth = false): void {
    this.anchor = null;
    const s = Math.exp(this.logSpan.target);
    const per = s / (this.plot.width * (1 - 2 * ROLL));
    const c = this.uOf(this.centre.target) + dx * per;
    const bounded = this.clampCentre(c, s);
    let u = bounded;
    if (this.held && !smooth) {
      // Rubber band: past an end, each px of drag moves the scroll less, up to a limit.
      const over = c - bounded;
      const limit = STRETCH * s;
      u = bounded + Math.sign(over) * limit * (1 - Math.exp(-Math.abs(over) / limit));
    }
    const year = this.yearOfU(u);
    if (smooth) {
      this.centre.halfLife = PACE.wheel;
      this.centre.target = year;
    } else this.centre.set(year);
  }

  /** Zooms time by a factor around a point on screen, keeping the year under it in place. */
  zoomAt(factor: number, px: number, smooth = true): void {
    // The year under the pointer as drawn right now, so moving the pointer mid-zoom never makes the view jump.
    const s = (px - this.plot.left) / this.plot.width;
    this.anchor = { year: this.yearAt(px), at: rollIn(s) };
    const span = this.clampSpan(Math.exp(this.logSpan.target) / factor);
    this.logSpan.halfLife = PACE.wheel;
    if (smooth) this.logSpan.target = Math.log(span);
    else this.logSpan.set(Math.log(span));
  }

  /** Flies to show a span of years, with some room either side. */
  frameYears(from: number, to: number, room = 0.04): void {
    this.anchor = null;
    const u0 = this.uOf(from),
      u1 = this.uOf(to);
    const s = this.clampSpan((u1 - u0) * (1 + 2 * room));
    this.logSpan.halfLife = this.centre.halfLife = PACE.dive;
    this.logSpan.target = Math.log(s);
    this.centre.target = this.yearOfU((u0 + u1) / 2);
  }

  /** While a realm is open its lanes are fixed; otherwise lanes may breathe. */
  private lanesHeld = false;

  /** Gives lanes relative heights (1 is the usual), unless a realm's lanes are open. */
  breathe(weights: readonly number[]): void {
    if (this.lanesHeld) return;
    this.weights.forEach((w, i) => (w.target = Math.log(weights[i] ?? 1)));
  }

  /**
   * Opens a realm in place. Its home lane grows to take `share` of the height, the other lands it held grow enough
   * to show its outline, and the rest narrow to thin bands. Where it holds only one half of its home, the line
   * between the halves slides to give its half most of the room.
   */
  focusRealm(home: { lane: number; part: "full" | "left" | "right" }, held: readonly number[], share = 0.84): void {
    this.lanesHeld = true;
    const others = held.filter((l) => l !== home.lane).length;
    const rest = this.weights.length - 1 - others;
    const heldWeight = 2.6;
    const homeWeight = (share * (others * heldWeight + rest)) / (1 - share);
    this.weights.forEach(
      (w, i) => (w.target = Math.log(i === home.lane ? homeWeight : held.includes(i) ? heldWeight : 1)),
    );
    this.splits.forEach(
      (sp, i) => (sp.target = i !== home.lane || home.part === "full" ? 0.5 : home.part === "left" ? 0.86 : 0.14),
    );
  }

  /** Brings one land forward: its lane takes most of the height, the others narrow to thin bands. */
  focusLand(lane: number, share = 0.66): void {
    this.focusRealm({ lane, part: "full" }, [lane], share);
  }

  /** Every land the same height again, its halves equal. */
  releaseLanes(): void {
    this.lanesHeld = false;
    for (const w of this.weights) w.target = 0;
    for (const sp of this.splits) sp.target = 0.5;
  }

  /** A snapshot of where the camera is heading, to come back to later. */
  bookmark(): { centre: number; logSpan: number } {
    return { centre: this.centre.target, logSpan: this.logSpan.target };
  }

  restore(b: { centre: number; logSpan: number }): void {
    this.anchor = null;
    this.logSpan.halfLife = this.centre.halfLife = PACE.dive;
    this.centre.target = b.centre;
    this.logSpan.target = b.logSpan;
  }
}

// The roll: s (0..1 across the plot) from t (0..1 across the time window), and back. The middle is flat; past
// either end of the window the scroll is wound up, so ever more years fit into the last few px. The slope is
// continuous where flat meets rolled, so nothing kinks as it passes over.
const K = ROLL / (1 - 2 * ROLL);

export function rollOut(t: number): number {
  if (t < 0) return ROLL * Math.exp(t / K);
  if (t > 1) return 1 - ROLL * Math.exp(-(t - 1) / K);
  return ROLL + t * (1 - 2 * ROLL);
}

export function rollIn(s: number): number {
  if (s < ROLL) return K * Math.log(Math.max(1e-9, s) / ROLL);
  if (s > 1 - ROLL) return 1 - K * Math.log(Math.max(1e-9, 1 - s) / ROLL);
  return (s - ROLL) / (1 - 2 * ROLL);
}
